import barba from '@barba/core';
import { gsap } from 'gsap';

export default class Header {
	constructor(elm) {
		this.elm = elm;

        this.logo = this.elm.querySelector('[data-logo]');

        this.menu = this.elm.querySelector('[data-menu]') ?? '';
        if(this.menu) {
            this.menuItems = this.menu.querySelectorAll('[data-menu-item]');
        }

        this.menuToggle = this.elm.querySelector('[data-menu-toggle]');
        this.menuOpen = false;
        this.menuToggle.addEventListener('click', () => {
            this.toggleMenu();
        });

        const mm = gsap.matchMedia();
        this.mm = mm.add({
            isMobile: `(max-width: 991px)`,
            isDesktop: `(min-width: 992px)`,
            reduceMotion: "(prefers-reduced-motion: reduce)"
          }, (context) => {

            let { isMobile, isDesktop, reduceMotion } = context.conditions;

            this.isMobile = isMobile;
            this.isDesktop = isDesktop;
            this.reduceMotion = reduceMotion;

            if(isMobile) {

                this.setKeyboardFocusableElements();

                gsap.set(this.menu, {
                    pointerEvents: 'none',
                    opacity: 0,
                });

                gsap.set(this.menuItems, {
                    opacity: 0,
                    yPercent: -100
                });
            }

        });

        barba.hooks.leave(({ current, next, trigger }) => {
            if(!this.menuItems) {
                return;
            }
            const nextNamespace = next.namespace;
            this.menuItems.forEach(item => {
                item.classList.remove('is-active');
                const itemNamespace = item.getAttribute('data-target-namespace');
                if(itemNamespace===nextNamespace) {
                    item.classList.add('is-active');
                }
            });

		});

        barba.hooks.after(({ current, next, trigger }) => {
            setTimeout(() => {
                if(this.menuOpen) {
                    this.closeMenu();
                }
            }, 500);
        });

        document.addEventListener('show-menu', () => {
			this.showMenu();
		});

        document.addEventListener('hide-menu', () => {
			this.hideMenu();
		});

        document.addEventListener('show-gradient', () => {
			this.showGradient();
		});

        document.addEventListener('hide-gradient', () => {
			this.hideGradient();
		});

	}

    setKeyboardFocusableElements() {
        this.getKeyboardFocusableElements = [];

		[...this.menuItems].forEach(item => {
			this.getKeyboardFocusableElements.push(item);
		});

        this.getKeyboardFocusableElements.forEach((element) => (element.tabIndex = -1));

        this.firstFocusableEl = this.getKeyboardFocusableElements[0];
		this.lastFocusableEl = this.getKeyboardFocusableElements[this.getKeyboardFocusableElements.length - 1];
    }

    showMenu() {
        this.elm.classList.remove('c-header--disabled');

        gsap.to(this.elm, {
            autoAlpha: 1,

            duration: 0.3,
            ease: 'none'
        });

        if(!this.isMobile) {
            this.menuItems.forEach((element) => element.removeAttribute('tabindex'));
        }
        this.logo.removeAttribute('tabindex');
        this.menuToggle.removeAttribute('tabindex');
    }

    hideMenu() {
        this.elm.classList.add('c-header--disabled');

        gsap.to(this.elm, {
            autoAlpha: 0,

            duration: 0.3,
            ease: 'none'
        });

        if(!this.isMobile) {
            this.menuItems.forEach((element) => (element.tabIndex = -1));
        }
        this.logo.setAttribute('tabindex', -1);
        this.menuToggle.setAttribute('tabindex', -1);
    }

    toggleMenu() {
        this.menuOpen ? this.closeMenu() : this.openMenu();
    }

    hideGradient() {
        gsap.to(this.elm, {
            "--gradientOpacity": 0,
            ease: 'none',
            duration: 0.5,
        });
    }

    showGradient() {
        gsap.to(this.elm, {
            "--gradientOpacity": 1,
            ease: 'none',
            duration: 0.5,
        });
    }

    openMenu() {
        this.menuOpen = true;

        if(this.isMobile) {
            gsap.set(this.menu, {
                pointerEvents: 'all'
            });

            gsap.to(this.menu, {
                opacity: 1,

                ease: 'none',
                duration: 0.5,
                onComplete: () => {
                    gsap.to(this.menuItems, {
                        opacity: 1,
                        yPercent: 0,

                        ease: 'power2.inOut',
                        duration: 0.5,
                        stagger: {
                            each: 0.15,
                            ease: 'power2.inOut'
                        },
                    });
                }
            });
        }

        this.menuToggle.classList.add('is-active');
        this.menuToggle.setAttribute('aria-expanded', true);
		this.menuToggle.setAttribute('aria-label', 'Close menu');

        this.trapFocus();
        document.dispatchEvent(new CustomEvent('disable-scroll'));
    }

    closeMenu() {
        this.menuOpen = false;

        if(this.isMobile) {
            gsap.set(this.menu, {
                pointerEvents: 'none'
            });

            gsap.to(this.menuItems, {
                opacity: 0,
                yPercent: -100,

                ease: 'power2.inOut',
                duration: 0.5,
                stagger: {
                    each: 0.15,
                    from: 'end',
                    ease: 'power2.inOut'
                },
                onComplete: () => {
                    gsap.to(this.menu, {
                        opacity: 0,
                        ease: 'none',
                        duration: 0.5
                    });
                }
            });

            this.menuToggle.classList.remove('is-active');
            this.menuToggle.setAttribute('aria-expanded', false);
            this.menuToggle.setAttribute('aria-label', 'Open menu');

            this.removeTrapFocus();
            document.dispatchEvent(new CustomEvent('enable-scroll'));
        }
    }

    trapFocus() {
		this.getKeyboardFocusableElements.forEach((element) => element.removeAttribute('tabindex'));

		this.tabfocussed = (event) => this.tabFocus(event);
		document.addEventListener('keydown', this.tabfocussed);
	}

    tabFocus(e) {
		if (e.key === 'Escape') {
			this.closeMenu();
			this.menuToggle.focus();
		}

		const KEYCODE_TAB = 9;
		const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			if (document.activeElement === this.firstFocusableEl) {
				this.lastFocusableEl.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === this.lastFocusableEl) {
				this.firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	}

    removeTrapFocus() {
		this.getKeyboardFocusableElements.forEach((element) => (element.tabIndex = -1));
		document.removeEventListener('keydown', this.tabfocussed);
	}

}